import { lumi } from '@/lib/lumi'
import { useState } from 'react'

interface SATCalculation {
  _id: string
  userId: string
  calculationType: 'digital_sat' | 'practice_test' | 'section_calculator'
  mathRaw: number
  readingRaw: number
  mathScaled: number
  readingScaled: number
  totalScore: number
  testNumber?: string
  notes?: string
  creator: string
  createdAt: string
  updatedAt: string
}

export function useSATCalculations() {
  const [calculations, setCalculations] = useState<SATCalculation[]>([])
  const [loading, setLoading] = useState(false)

  const fetchCalculations = async () => {
    setLoading(true)
    try {
      const { list } = await lumi.entities.sat_calculations.list({
        sort: { createdAt: -1 },
        limit: 50
      })
      setCalculations(list)
      console.log('SAT calculations fetched:', list)
    } catch (error) {
      console.error('Failed to fetch calculations:', error)
    } finally {
      setLoading(false)
    }
  }

  const saveCalculation = async (data: {
    calculationType: string
    mathRaw: number
    readingRaw: number
    mathScaled: number
    readingScaled: number
    totalScore: number
    testNumber?: string
    notes?: string
  }) => {
    try {
      const user = lumi.auth.user
      if (!user) {
        console.warn('User not authenticated, skipping save')
        return null
      }

      const calculation = await lumi.entities.sat_calculations.create({
        ...data,
        userId: user.userId,
        creator: user.userId,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      })
      console.log('Calculation saved:', calculation)
      await fetchCalculations()
      return calculation
    } catch (error) {
      console.error('Failed to save calculation:', error)
      throw error
    }
  }

  const updateCalculation = async (id: string, updates: Partial<SATCalculation>) => {
    try {
      if (typeof id !== 'string') {
        throw new Error('ID must be a string')
      }
      const updated = await lumi.entities.sat_calculations.update(id, {
        ...updates,
        updatedAt: new Date().toISOString()
      })
      console.log('Calculation updated:', updated)
      await fetchCalculations()
      return updated
    } catch (error) {
      console.error('Failed to update calculation:', error)
      throw error
    }
  }

  const deleteCalculation = async (id: string) => {
    try {
      if (typeof id !== 'string' || id === '[object Object]') {
        throw new Error('Invalid ID: must be a string, not an object')
      }
      await lumi.entities.sat_calculations.delete(id)
      console.log('Calculation deleted:', id)
      await fetchCalculations()
    } catch (error) {
      console.error('Failed to delete calculation:', error)
      throw error
    }
  }

  return {
    calculations,
    loading,
    fetchCalculations,
    saveCalculation,
    updateCalculation,
    deleteCalculation
  }
}
